import axios from 'axios';
import { ScanRequest, ScanResult, ApiResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production'
  ? '/api'
  : '/api');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds timeout for scans
  headers: {
    'Content-Type': 'application/json',
  },
});



// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const apiService = {
  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // Scan website
  async scanWebsite(request: ScanRequest): Promise<ScanResult> {
    const response = await apiClient.post<ApiResponse<ScanResult>>('/scan', request);

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.error?.message || 'Scan failed');
    }
  },

  // Get scan history (placeholder for future feature)
  async getScanHistory(): Promise<any[]> {
    const response = await apiClient.get('/history');
    return response.data.scans || [];
  },

  // Create Stripe Checkout session
  async createCheckoutSession(scanId: string): Promise<{ id: string; url: string }> {
    const response = await apiClient.post('/payment/create-checkout-session', { scanId });
    return response.data;
  },

  // Verify if a scan is paid
  async verifyPayment(scanId: string): Promise<{ isPaid: boolean }> {
    const response = await apiClient.get(`/reports/verify/${scanId}`);
    return response.data;
  },

  // Confirm payment (client-side trial)
  async confirmPayment(scanId: string): Promise<void> {
    await apiClient.post('/reports/confirm-payment', { scanId });
  },

  // Submit contact form
  async submitContactForm(request: any): Promise<void> {
    const response = await apiClient.post<ApiResponse<any>>('/contact', request);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to send message');
    }
  },
};

// Error handling helper
// Error handling helper
export const handleApiError = (error: any): { message: string; code?: string } => {
  if (error.response?.data?.error) {
    const apiError = error.response.data.error;
    return {
      message: apiError.message || 'An unknown error occurred',
      code: apiError.code
    };
  }

  if (error.code === 'ECONNABORTED') {
    return { message: 'Request timed out. The website might be taking too long to respond.' };
  }

  if (error.message?.includes('Network Error')) {
    return { message: 'Network error. Please check your internet connection and try again.' };
  }

  return { message: error.message || 'An unexpected error occurred' };
};
